import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Activity, Smile, AlertTriangle } from 'lucide-react';
import { formatDate } from '../lib/utils';

export default function CheckinsPage() {
  const { user, axiosAuth } = useAuth();
  const [checkins, setCheckins] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const api = axiosAuth();

  const canViewAll = ['admin', 'coordinator', 'peer_specialist', 'network_admin'].includes(user.role);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [checkinsRes, clientsRes] = await Promise.all([
          api.get(canViewAll ? '/checkins?all=true' : '/checkins'),
          canViewAll ? api.get('/users?role=client').catch(() => ({ data: [] })) : Promise.resolve({ data: [] })
        ]);
        setCheckins(checkinsRes.data || []);
        setClients(clientsRes.data || []);
      } catch {
        setCheckins([]);
      }
      setLoading(false);
    };
    load();
  }, [canViewAll]);

  const clientLookup = useMemo(() => {
    const map = {};
    clients.forEach(c => { map[c.id] = c; });
    return map;
  }, [clients]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-3 border-teal-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="checkins-page">
      <div>
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>Check-in Activity</h1>
        <p className="text-muted-foreground text-sm mt-1">Recent daily check-ins across participants</p>
      </div>

      <div className="space-y-3">
        {checkins.length > 0 ? checkins.map(checkin => {
          const client = clientLookup[checkin.user_id];
          return (
            <Card key={checkin.id} className="border-0 shadow-sm rounded-2xl" data-testid={`checkin-row-${checkin.id}`}>
              <CardContent className="p-4 flex flex-col md:flex-row md:items-center gap-3">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center shrink-0">
                    <Smile className="w-5 h-5 text-teal-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium truncate">{client?.full_name || 'Participant'}</p>
                    <p className="text-xs text-muted-foreground">{formatDate(checkin.date)}</p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <Badge className="bg-sky-50 text-sky-700" variant="secondary" data-testid={`checkin-mood-${checkin.id}`}>Mood: {checkin.mood ?? 'N/A'}</Badge>
                  <Badge className="bg-amber-50 text-amber-700" variant="secondary" data-testid={`checkin-cravings-${checkin.id}`}>Cravings: {checkin.cravings ?? 'N/A'}</Badge>
                  <Badge className={checkin.stayed_sober ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'} variant="secondary" data-testid={`checkin-sober-${checkin.id}`}>
                    {checkin.stayed_sober ? 'Sober' : 'Needs Support'}
                  </Badge>
                  {checkin.attended_peer_support && (
                    <Badge className="bg-violet-50 text-violet-700" variant="secondary" data-testid={`checkin-peer-${checkin.id}`}>
                      Peer Support
                    </Badge>
                  )}
                </div>
              </CardContent>
              {checkin.notes && (
                <CardContent className="pt-0 px-4 pb-4">
                  <div className="rounded-xl bg-muted/30 p-3 text-sm" data-testid={`checkin-notes-${checkin.id}`}>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                      <Activity className="w-3 h-3" /> Notes
                    </div>
                    {checkin.notes}
                  </div>
                </CardContent>
              )}
            </Card>
          );
        }) : (
          <Card className="border-0 shadow-sm rounded-2xl">
            <CardContent className="p-8 text-center">
              <AlertTriangle className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No check-ins found.</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
